import { Component, ElementRef, HostListener, NgZone, OnInit, ViewChild } from '@angular/core';
import { DesignLanguage, SidePanelCard } from '../../models/side-panel-card';
import { SIDE_PANEL_PREVIEW_ORIGINS } from '../side-panel-cards/side-panel-config';

/**
 * Bare page for the Blitz admin editor's iframe: paints draft cards with the same component the course page uses.
 *
 * Messages (only from SIDE_PANEL_PREVIEW_ORIGINS):
 *   admin  -> iframe  { type: 'sp-preview', cards: string[], designLanguage: 'CLASSIC' | 'PRISM' }
 *   iframe -> admin   { type: 'sp-ready' } once on load, { type: 'sp-height', px } after every render
 */
@Component({
  selector: 'app-side-panel-preview',
  template: '<div #content class="sp-preview"><app-side-panel-cards [cards]="cards" (rendered)="onRendered()"></app-side-panel-cards></div>',
  styles: [':host { display: block; background: #fff; } .sp-preview { display: flow-root; padding: 2px 6px 18px; }']
})
export class SidePanelPreviewComponent implements OnInit {

  @ViewChild('content', { static: true }) content: ElementRef<HTMLDivElement>;

  cards: SidePanelCard[] = [];
  private adminOrigin: string = null;

  constructor(private zone: NgZone) { }

  ngOnInit(): void {
    // The frame is sized to the cards, so the page itself never scrolls.
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    if (window.parent !== window) {
      SIDE_PANEL_PREVIEW_ORIGINS.forEach(origin => window.parent.postMessage({ type: 'sp-ready' }, origin));
    }
    // Images and fonts change the height after the first paint; report every change of the cards' own box.
    const resizeObserver = (window as any).ResizeObserver;
    if (resizeObserver) {
      new resizeObserver(() => this.postHeight()).observe(this.content.nativeElement);
    }
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent): void {
    if (SIDE_PANEL_PREVIEW_ORIGINS.indexOf(event.origin) < 0) {
      return;
    }
    const data = event.data;
    if (!data || data.type !== 'sp-preview' || !Array.isArray(data.cards)) {
      return;
    }
    this.adminOrigin = event.origin;
    const designLanguage: DesignLanguage = data.designLanguage === 'PRISM' ? 'PRISM' : 'CLASSIC';
    this.zone.run(() => {
      this.cards = data.cards
        .filter((html: any) => typeof html === 'string' && html.trim().length > 0)
        .map((html: string) => ({ html, designLanguage }));
    });
  }

  onRendered(): void {
    // Wait a frame so the new cards are laid out before measuring.
    requestAnimationFrame(() => this.postHeight());
  }

  private postHeight(): void {
    if (!this.adminOrigin) {
      return;
    }
    // Measure the cards, not the body: the app shell stretches the body to the frame height.
    const px = Math.ceil(this.content.nativeElement.getBoundingClientRect().height);
    window.parent.postMessage({ type: 'sp-height', px }, this.adminOrigin);
  }
}
